import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Typography from "@mui/material/Typography";
import { useTheme } from "@mui/material/styles";
import { ChartEmptyState } from "@/shared/charts";
import { ConsoleSurface } from "@/shared/ui";
import type { AreaLocation } from "@/features/deployment-dashboard/types";
import {
  getThroughputLineOptionKey,
  type ThroughputLineOption,
} from "@/features/deployment-dashboard/widgets/charts/throughput-trend/types";
import { ThroughputAreaLineMap } from "@/features/deployment-dashboard/widgets/panels/throughput-line-map/throughput-area-line-map";

export interface ThroughputLineMapProps {
  deploymentId: string;
  areas: AreaLocation[];
  lines: ThroughputLineOption[];
  selectedLineKeys: ReadonlySet<string>;
  hoveredLineKey: string | null;
}

interface AreaLineGroup {
  area: Pick<AreaLocation, "id" | "name">;
  lines: ThroughputLineOption[];
}

function LineCountMarkerIcon() {
  const theme = useTheme();
  const markerRows = [12, 39, 66];

  return (
    <Box
      component="svg"
      viewBox="0 0 48 84"
      aria-hidden="true"
      sx={{ width: 48, height: 84, flexShrink: 0 }}
    >
      <line x1="24" y1="2" x2="24" y2="82" stroke={theme.palette.text.primary} strokeWidth="3" />
      {markerRows.map((row) => (
        <g key={row}>
          <polygon
            points={`2,${row} 21,${row - 10} 21,${row + 10}`}
            fill={theme.palette.error.main}
          />
          <polygon
            points={`46,${row} 27,${row - 10} 27,${row + 10}`}
            fill={theme.palette.success.main}
          />
        </g>
      ))}
    </Box>
  );
}

function LineCountMarkerInfo() {
  return (
    <Alert
      severity="info"
      aria-label="Line count marker information"
      sx={{
        alignItems: "flex-start",
        minWidth: 0,
        py: 1,
        "& .MuiAlert-message": { width: "100%" },
      }}
    >
      <Box sx={{ display: "flex", flexDirection: "column", gap: 1.25, width: "100%" }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.25 }}>
          <LineCountMarkerIcon />
          <Box sx={{ display: "flex", flexDirection: "column", gap: 0.25 }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 700, lineHeight: 1.2 }}>
              Line Count Marker
            </Typography>
            <Box sx={{ display: "flex", flexWrap: "wrap", columnGap: 1.5, rowGap: 0.25 }}>
              <Typography variant="body2" color="success.main">
                Green = In
              </Typography>
              <Typography variant="body2" color="error.main">
                Red = Out
              </Typography>
            </Box>
          </Box>
        </Box>
        <Box sx={{ display: "flex", flexDirection: "column", gap: 0.75 }}>
          <Typography variant="body2">
            Count for people entering IDL is calculated based on the number leaving CSA.
          </Typography>
          <Typography variant="body2">
            Total population in IDL calculated as net number entering through (1) minus the net
            number leaving through (3) and (4).
          </Typography>
        </Box>
      </Box>
    </Alert>
  );
}

function LineSelectionSampleIcon({ selected }: { selected: boolean }) {
  const theme = useTheme();
  const colors = selected
    ? {
        in: theme.palette.success.main,
        out: theme.palette.error.main,
      }
    : {
        in: theme.palette.grey[300],
        out: theme.palette.grey[700],
      };

  return (
    <Box
      component="svg"
      viewBox="0 0 48 32"
      aria-hidden="true"
      sx={{ width: 48, height: 32, flexShrink: 0 }}
    >
      <line x1="24" y1="2" x2="24" y2="30" stroke={theme.palette.common.white} strokeWidth="3" />
      <polygon points="2,16 21,6 21,26" fill={colors.out} />
      <polygon points="46,16 27,6 27,26" fill={colors.in} />
    </Box>
  );
}

function LineSelectionInfo() {
  return (
    <Alert
      severity="info"
      aria-label="Line selection information"
      sx={{
        alignItems: "flex-start",
        minWidth: 0,
        py: 1,
        "& .MuiAlert-message": { width: "100%" },
      }}
    >
      <Box sx={{ display: "flex", flexDirection: "column", gap: 0.75 }}>
        <Typography variant="body2" sx={{ fontWeight: 700 }}>
          Line selection
        </Typography>
        <Typography variant="body2">
          Selected lines use active colours. Unselected lines remain visible in grayscale.
        </Typography>
        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1.5 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
            <LineSelectionSampleIcon selected />
            <Typography variant="caption">Selected</Typography>
          </Box>
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
            <LineSelectionSampleIcon selected={false} />
            <Typography variant="caption">Unselected</Typography>
          </Box>
        </Box>
      </Box>
    </Alert>
  );
}

/** Groups all configured throughput lines and renders one independent floor plan per area. */
export function ThroughputLineMap({
  deploymentId,
  areas,
  lines,
  selectedLineKeys,
  hoveredLineKey,
}: ThroughputLineMapProps) {
  const [isMapVisible, setIsMapVisible] = useState(true);
  const [areaImageSizes, setAreaImageSizes] = useState(
    () => new Map<string, { width: number; height: number }>()
  );
  const stripRef = useRef<HTMLDivElement>(null);
  const areaCardRefs = useRef(new Map<string, HTMLDivElement>());

  const areaGroups = useMemo<AreaLineGroup[]>(() => {
    const areaNames = new Map(areas.map((area) => [area.id, area.name]));
    const groups = new Map<string, ThroughputLineOption[]>();

    for (const line of lines) {
      const areaLines = groups.get(line.areaId) ?? [];
      areaLines.push(line);
      groups.set(line.areaId, areaLines);
    }

    return [...groups.entries()]
      .sort(([firstAreaId], [secondAreaId]) => {
        const firstIndex = areas.findIndex((area) => area.id === firstAreaId);
        const secondIndex = areas.findIndex((area) => area.id === secondAreaId);
        return (
          (firstIndex < 0 ? Number.MAX_SAFE_INTEGER : firstIndex) -
          (secondIndex < 0 ? Number.MAX_SAFE_INTEGER : secondIndex)
        );
      })
      .map(([areaId, lines]) => ({
        area: { id: areaId, name: areaNames.get(areaId) ?? lines[0]?.areaName ?? areaId },
        lines,
      }));
  }, [areas, lines]);

  const handleFloorplanSizeChange = useCallback(
    (areaId: string, size: { width: number; height: number }) => {
      setAreaImageSizes((current) => {
        const previous = current.get(areaId);
        if (previous?.width === size.width && previous.height === size.height) return current;

        const next = new Map(current);
        next.set(areaId, size);
        return next;
      });
    },
    []
  );

  useEffect(() => {
    if (hoveredLineKey == null) return;

    const hoveredLine = lines.find((line) => getThroughputLineOptionKey(line) === hoveredLineKey);
    const strip = stripRef.current;
    const areaCard = hoveredLine == null ? undefined : areaCardRefs.current.get(hoveredLine.areaId);
    if (strip == null || areaCard == null) return;

    const stripRect = strip.getBoundingClientRect();
    const cardRect = areaCard.getBoundingClientRect();
    const visibilityMargin = 8;
    const isVisible =
      cardRect.left >= stripRect.left + visibilityMargin &&
      cardRect.right <= stripRect.right - visibilityMargin;
    if (isVisible) return;

    const targetLeft = areaCard.offsetLeft - (strip.clientWidth - areaCard.clientWidth) / 2;
    strip.scrollTo({ left: Math.max(0, targetLeft), behavior: "smooth" });
  }, [hoveredLineKey, lines, areaGroups]);

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        gap: 1,
        pt: 1.5,
        borderTop: 1,
        borderColor: "divider",
      }}
    >
      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
        <Typography
          component="h3"
          variant="overline"
          sx={{ flexGrow: 1, fontWeight: 700, letterSpacing: "0.1em", color: "text.secondary" }}
        >
          Line Preview
        </Typography>
        <Button
          size="small"
          onClick={() => setIsMapVisible((visible) => !visible)}
          aria-expanded={isMapVisible}
          endIcon={isMapVisible ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
        >
          {isMapVisible ? "Hide map" : "Show map"}
        </Button>
      </Box>

      {isMapVisible && (
        <Box
          sx={{
            display: { xs: "flex", xl: "grid" },
            flexDirection: "column",
            gridTemplateColumns: "minmax(280px, 0.36fr) minmax(0, 1fr)",
            gap: { xs: 1.5, xl: 2.5 },
            alignItems: "stretch",
          }}
        >
          <Box
            sx={{
              minWidth: 0,
              pr: { xs: 0, xl: 2.5 },
              borderRight: { xs: 0, xl: 1 },
              borderColor: "divider",
            }}
          >
            <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
              <LineCountMarkerInfo />
              <LineSelectionInfo />
            </Box>
          </Box>
          <Box sx={{ width: "100%", minWidth: 0 }}>
            {areaGroups.length === 0 ? (
              <ChartEmptyState
                title="No throughput lines configured"
                description="Configure a throughput line for an area to view its floor plan."
              />
            ) : (
              <Box
                ref={stripRef}
                sx={{
                  display: "flex",
                  gap: 1.5,
                  minHeight: 0,
                  overflowX: "auto",
                  overflowY: "hidden",
                  pb: 0.5,
                }}
              >
                {areaGroups.map(({ area, lines }) => {
                  const imageSize = areaImageSizes.get(area.id);
                  const aspectRatio =
                    imageSize == null ? undefined : imageSize.width / imageSize.height;
                  const cardWidth =
                    aspectRatio == null
                      ? undefined
                      : Math.min(720, Math.max(360, 360 * aspectRatio));

                  return (
                    <Box
                      key={area.id}
                      ref={(element: HTMLDivElement | null) => {
                        if (element == null) areaCardRefs.current.delete(area.id);
                        else areaCardRefs.current.set(area.id, element);
                      }}
                      sx={{
                        flex:
                          areaGroups.length === 1
                            ? "1 1 100%"
                            : cardWidth == null
                              ? "0 0 min(720px, 78vw)"
                              : `0 0 ${cardWidth}px`,
                        minWidth: 0,
                      }}
                    >
                      <ConsoleSurface
                        sx={{
                          display: "flex",
                          flexDirection: "column",
                          gap: 0.75,
                          p: 1.5,
                          minWidth: 0,
                        }}
                      >
                        <Typography component="h4" variant="subtitle2" sx={{ fontWeight: 700 }}>
                          {area.name}
                        </Typography>
                        <ThroughputAreaLineMap
                          deploymentId={deploymentId}
                          area={area}
                          lines={lines}
                          selectedLineKeys={selectedLineKeys}
                          hoveredLineKey={hoveredLineKey}
                          onFloorplanSizeChange={handleFloorplanSizeChange}
                        />
                      </ConsoleSurface>
                    </Box>
                  );
                })}
              </Box>
            )}
          </Box>
        </Box>
      )}
    </Box>
  );
}
